import { ReactNode, useEffect, useState } from 'react';
import { FormProvider, UseFormReturn, useForm } from 'react-hook-form';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore lodash is in the parent dir
import _ from 'lodash';

import { TooltipProvider } from 'components/Tooltip';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { Maybe } from 'types/UtilityTypes';

import { QuickQuoteInputs } from '../types';
import { BatchLTLShipmentInput } from './types';

export type BatchLTLShipmentFormMethods = UseFormReturn<
  QuickQuoteInputs,
  any,
  QuickQuoteInputs
>;

type BatchLTLShipmentFormProps = {
  // Values the form starts from; later changes flow up through onValuesChange.
  shipment: BatchLTLShipmentInput;
  onRegister: (
    key: number,
    formMethods: Maybe<BatchLTLShipmentFormMethods>
  ) => void;
  onValuesChange: (key: number, values: QuickQuoteInputs) => void;
  children: ReactNode;
};

// One shipment's own Quick Quote form, so its card can render the single LTL (Tai) form
// sections unchanged. The form stays mounted while the card is collapsed; the panel reads
// its values through onValuesChange and edits it (bulk edit, Shiplify) through the
// registered form methods.
export function BatchLTLShipmentForm({
  shipment,
  onRegister,
  onValuesChange,
  children,
}: BatchLTLShipmentFormProps) {
  const [defaultValues] = useState(() => shipment);
  const formMethods = useForm<QuickQuoteInputs>({ defaultValues });
  const { key } = shipment;

  useEffect(() => {
    onRegister(key, formMethods);
    return () => onRegister(key, null);
  }, [key, formMethods, onRegister]);

  useEffect(() => {
    const subscription = formMethods.watch(() =>
      onValuesChange(key, _.cloneDeep(formMethods.getValues()))
    );

    return () => subscription.unsubscribe();
  }, [key, formMethods, onValuesChange]);

  // The same providers the single Quick Quote form puts around its LTL sections.
  return (
    <ExtendedFormProvider aiDefaultValues={true}>
      <FormProvider {...formMethods}>
        <TooltipProvider>{children}</TooltipProvider>
      </FormProvider>
    </ExtendedFormProvider>
  );
}
